/*
 * Verenna — marketing landing.
 *
 * Content is ported from the product's own landing surface so this page and
 * the app tell the same story in the same design language.
 *
 * Two honesty rules this page keeps:
 *  - No fabricated customer logos. The "built for" strip names organization
 *    TYPES, not customers we don't have.
 *  - No unsubstantiated trust claims (no certifications, no "never trains a
 *    model", no unqualified encryption claims) until they can be backed.
 *
 * Every call to action reaches a real inbox. Nothing here links to a surface
 * that does not exist yet.
 */

const BRAND = "Verenna";
const CONTACT_EMAIL = "hello@aquamirecapital.com";
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  `${BRAND} — early access`,
)}`;

/* ------------------------------------------------------------------ icons */

const svg = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

const Icon = ({ size = 20, children }) => (
  <svg {...svg} width={size} height={size}>
    {children}
  </svg>
);

const Sparkles = (p) => (
  <Icon {...p}>
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  </Icon>
);
const Users = (p) => (
  <Icon {...p}>
    <path d="M18 21a8 8 0 0 0-16 0" />
    <circle cx="10" cy="8" r="5" />
    <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
  </Icon>
);
const Bell = (p) => (
  <Icon {...p}>
    <path d="M10.268 21a2 2 0 0 0 3.464 0" />
    <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
  </Icon>
);
const Route = (p) => (
  <Icon {...p}>
    <circle cx="6" cy="19" r="3" />
    <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
    <circle cx="18" cy="5" r="3" />
  </Icon>
);
const Send = (p) => (
  <Icon {...p}>
    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
    <path d="m21.854 2.147-10.94 10.939" />
  </Icon>
);
const Clipboard = (p) => (
  <Icon {...p}>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="m9 14 2 2 4-4" />
  </Icon>
);
const Shield = (p) => (
  <Icon {...p}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </Icon>
);
const Mic = (p) => (
  <Icon {...p}>
    <path d="M12 19v3" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <rect x="9" y="2" width="6" height="13" rx="3" />
  </Icon>
);
const ArrowRight = (p) => (
  <Icon {...p}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </Icon>
);
const Check = (p) => (
  <Icon {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);
const X = (p) => (
  <Icon {...p}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Icon>
);

/* ------------------------------------------------------------------- data */

const CAPABILITIES = [
  {
    icon: Sparkles,
    title: "Capture",
    body: "A text, a link, a note on the walk home. Getting a new person in takes seconds, not a form.",
  },
  {
    icon: Users,
    title: "One CRM",
    body: "Every person, household, and relationship in a single spine — nickname- and alternate-name aware.",
  },
  {
    icon: Bell,
    title: "Retain",
    body: "Nobody drifts silently. Time- and life-event triggers surface who needs a touch, before they're gone.",
  },
  {
    icon: Route,
    title: "Advance",
    body: "Journeys guide a relationship from first hello to committed — and step forward as you do the work.",
  },
  {
    icon: Send,
    title: "Reach",
    body: "Segment and message the right group — a class, a route, a cohort — from one place.",
  },
  {
    icon: Clipboard,
    title: "Report",
    body: "Funder-ready in minutes. The numbers come from the work already logged, not a year-end scramble.",
  },
];

const VERTICALS = [
  "Nonprofits & food banks",
  "Faith communities & outreach",
  "Synagogues",
  "Day schools",
  "Camps",
  "Hillels & campus",
];

const BUILT_FOR = ["Nonprofits", "Faith communities", "Schools", "Camps", "Campus groups"];

const WITHOUT = [
  "Contacts scattered across a spreadsheet, a donor CRM, and someone's phone",
  "Follow-ups remembered by hand — until they aren't",
  "The same person is a “participant” in one system, a “donor” in another",
  "Year-end reporting means weeks of spreadsheet surgery",
];

const WITH = [
  "One record per person — history, relationships, and giving together",
  "The right follow-up surfaces itself, on time, on your phone",
  "Journeys move people forward automatically as you go",
  "The report writes itself from the work you already did",
];

const TRUST = [
  {
    title: "Yours, and private",
    body: "Your data stays yours. We don't sell it or hand it to anyone else's product.",
  },
  {
    title: "Built for sensitive information",
    body: "Designed from the ground up for the kind of personal and community data that deserves extra care.",
  },
  {
    title: "Every change is recorded",
    body: "A complete audit trail means you always know who did what, and when.",
  },
];

const MOCK_ROWS = [
  { initials: "PA", name: "Priya Anand", meta: "No contact in 3 weeks" },
  { initials: "MB", name: "Marcus Bell", meta: "Follow-up promised Tuesday" },
  { initials: "LO", name: "Leah Osei", meta: "New this week · needs a welcome" },
];

/* ------------------------------------------------------------- components */

const Eyebrow = ({ children, muted }) => (
  <p className={muted ? "eyebrow eyebrow--muted" : "eyebrow"}>{children}</p>
);

const Wordmark = ({ small }) => (
  <span className="wordmark" style={small ? { fontSize: "1.1875rem" } : undefined}>
    <span className={small ? "mark mark--sm" : "mark"}>
      <Sparkles size={small ? 15 : 17} />
    </span>
    {BRAND}
  </span>
);

/* -------------------------------------------------------------------- app */

export default function App() {
  return (
    <>
      <header className="nav">
        <div className="wrap nav__inner">
          <Wordmark />
          <nav className="nav__links" aria-label="Sections">
            <a href="#product">Product</a>
            <a href="#how">How it works</a>
            <a href="#verticals">Verticals</a>
            <a href="#trust">Security</a>
          </nav>
          <div className="nav__actions">
            <a className="btn btn--sm btn--ghost" href={MAILTO}>
              Contact
            </a>
            <a className="btn btn--sm btn--primary" href={MAILTO}>
              Request access
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="wrap hero">
          <div>
            <span className="badge">
              <Sparkles size={14} /> One system for the whole relationship
            </span>
            <h1>Your mission runs on relationships. Now your software does too.</h1>
            <p className="hero__sub">
              {BRAND} is the operating system for any organization whose work <em>is</em> people —
              capture, remember, follow up, and report, all in one place. The database stays
              invisible; the relationship stays front and center.
            </p>
            <div className="hero__cta">
              <a className="btn btn--lg btn--primary" href={MAILTO}>
                Request early access <ArrowRight size={17} />
              </a>
              <a className="btn btn--lg btn--outline" href="#how">
                See how it works
              </a>
            </div>
            <p className="hero__note">
              Replaces the spreadsheet + separate donor CRM + reminder-app stack. Your data stays
              yours.
            </p>
          </div>

          <div className="mock" aria-hidden="true">
            <div className="mock__head">
              <p>Good morning, Dana</p>
              <p className="mock__org">Northside Center</p>
            </div>
            <p className="mock__label">Today · 12 due</p>
            <div className="mock__list">
              {MOCK_ROWS.map((r) => (
                <div className="mock__row" key={r.initials}>
                  <span className="mock__avatar">{r.initials}</span>
                  <div style={{ minWidth: 0 }}>
                    <p className="mock__name">{r.name}</p>
                    <p className="mock__meta">{r.meta}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mock__foot">
              <span className="mock__mic">
                <Mic size={24} />
              </span>
            </div>
          </div>
        </section>

        {/* Built for */}
        <section className="wrap builtfor">
          <Eyebrow muted>Built for organizations that run on relationships</Eyebrow>
          <div className="builtfor__list">
            {BUILT_FOR.map((v) => (
              <span key={v}>{v}</span>
            ))}
          </div>
        </section>

        {/* Problem */}
        <section id="product" className="wrap section">
          <Eyebrow>The problem</Eyebrow>
          <h2 className="h2">Your relationships live in five tools that don&rsquo;t talk.</h2>
          <p className="lede">
            A donor CRM here, a spreadsheet of contacts there, sticky-note reminders, a messaging
            app, and weeks each year translating it all into a report. People fall through the
            cracks — not for lack of caring, but for lack of one place.
          </p>
          <div className="grid grid--2">
            <div className="card card--muted">
              <h3 className="card__title-row card__title-row--warning">
                <X size={17} /> Today, without {BRAND}
              </h3>
              <ul className="checklist checklist--warning">
                {WITHOUT.map((t) => (
                  <li key={t}>
                    <X size={16} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card">
              <h3 className="card__title-row card__title-row--success">
                <Check size={17} /> With {BRAND}
              </h3>
              <ul className="checklist checklist--success">
                {WITH.map((t) => (
                  <li key={t}>
                    <Check size={16} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section id="how" className="wrap section">
          <Eyebrow>What it does</Eyebrow>
          <h2 className="h2">Everything the relationship needs — in one operating system.</h2>
          <div className="grid grid--3">
            {CAPABILITIES.map(({ icon: CapIcon, title, body }) => (
              <div className="card" key={title}>
                <span className="card__icon">
                  <CapIcon size={21} />
                </span>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Verticals */}
        <section id="verticals" className="wrap section--tight">
          <div className="panel">
            <Eyebrow>Agnostic core, fitted to you</Eyebrow>
            <h2 className="h2">One engine. A packet for your world.</h2>
            <p className="lede">
              {BRAND}&rsquo;s core carries no assumptions about your field. A packet overlays the
              vocabulary, workflows, and reports your kind of organization actually uses — so it
              speaks your language on day one, without a custom build. Every vertical is a peer;
              none is the &ldquo;default.&rdquo;
            </p>
            <div className="chips">
              {VERTICALS.map((v) => (
                <span className="chip" key={v}>
                  {v}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Trust */}
        <section id="trust" className="wrap section">
          <Eyebrow>Trust</Eyebrow>
          <h2 className="h2">Your community&rsquo;s data, handled like it matters.</h2>
          <div className="grid grid--3">
            {TRUST.map((c) => (
              <div className="card" key={c.title}>
                <h3 className="card__title-row">
                  <Shield size={17} /> {c.title}
                </h3>
                <p>{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing */}
        <section className="wrap" style={{ paddingBottom: "6rem" }}>
          <div className="closing">
            <Eyebrow>Ready when you are</Eyebrow>
            <h2 className="h2">See your whole organization in one place.</h2>
            <p className="lede">Bring your spreadsheet — we&rsquo;ll show you the difference.</p>
            <div className="closing__cta">
              <a className="btn btn--lg btn--primary" href={MAILTO}>
                Request early access <ArrowRight size={17} />
              </a>
              <a className="btn btn--lg btn--outline" href={`mailto:${CONTACT_EMAIL}`}>
                Talk to us
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="wrap footer__inner">
          <div className="footer__brand">
            <Wordmark small />
            <p>The operating system for work that runs on relationships.</p>
          </div>
          <nav className="footer__col" aria-label="Product">
            <span>Product</span>
            <a href="#product">Overview</a>
            <a href="#how">Capabilities</a>
            <a href="#verticals">Verticals</a>
          </nav>
          <nav className="footer__col" aria-label="Company">
            <span>Company</span>
            <a href="#trust">Security</a>
            <a href={MAILTO}>Request access</a>
            <a href={`mailto:${CONTACT_EMAIL}`}>Contact</a>
          </nav>
        </div>
        <div className="wrap footer__legal">
          <p>© {new Date().getFullYear()} {BRAND}. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
}
